import { csvEscapeField } from "@redotech/redo-common-model-csv-common/escape-field";
import {
  ImportRowFormat,
  ITEM_LEVEL_FIELDS,
  OrderImportColumnMapping,
  OrderImportField,
} from "@redotech/redo-fulfillment-common/import-schema";
import { FulfillmentImportSource } from "../common/import-orders-modal";
import { AdultsAndCraftsCsvColumns } from "./parse-adults-and-crafts-csv";
import { ApparelMagicCsvColumns } from "./parse-apparel-magic-csv";
import { FortyBoxesCsvColumns } from "./parse-forty-boxes-csv";

interface CSVExample {
  headers: string[];
  sampleRows: string[][];
}

export function getCSVExample(
  importSource: FulfillmentImportSource,
): CSVExample {
  switch (importSource) {
    case FulfillmentImportSource.APPAREL_MAGIC:
      return getApparelMagicExample();
    case FulfillmentImportSource.FORTY_BOXES:
      return getFortyBoxesExample();
    case FulfillmentImportSource.KICKSTARTER:
      return getKickstarterExample();
    case FulfillmentImportSource.ADULTS_AND_CRAFTS:
      return getAdultsAndCraftsExample();
    default:
      return { headers: [], sampleRows: [] };
  }
}

function getApparelMagicExample(): CSVExample {
  const headers = Object.values(ApparelMagicCsvColumns);
  const sampleRows = [
    [
      "John Doe",
      "123 Main St",
      "Apt 2B",
      "New York",
      "NY",
      "10001",
      "10x8x6",
      "2.5",
    ],
    [
      "Jane Smith",
      "456 Oak Ave",
      "",
      "Los Angeles",
      "CA",
      "90210",
      "12x10x8",
      "3.0",
    ],
  ];

  return { headers, sampleRows };
}

function getFortyBoxesExample(): CSVExample {
  const headers = [
    FortyBoxesCsvColumns.NAME,
    FortyBoxesCsvColumns.STREET_ADDRESS,
    FortyBoxesCsvColumns.ADDRESS_2,
    FortyBoxesCsvColumns.CITY,
    FortyBoxesCsvColumns.STATE,
    FortyBoxesCsvColumns.ZIP,
    FortyBoxesCsvColumns.WEIGHT,
    "Product SKU's 1",
    "Product Quantity 1",
    "Product SKU's 2",
    "Product Quantity 2",
    "Product SKU's 3",
    "Product Quantity 3",
    "Product SKU's 4",
    "Product Quantity 4",
    "Product SKU's 5",
    "Product Quantity 5",
    FortyBoxesCsvColumns.ORDER_NUMBER,
    FortyBoxesCsvColumns.GIFT_MESSAGE,
    FortyBoxesCsvColumns.INTERNAL_NOTES,
  ];

  const sampleRows = [
    [
      "John Doe",
      "123 Main St",
      "Apt 2B",
      "New York",
      "NY",
      "10001",
      "2.5",
      "SKU-001",
      "2",
      "SKU-002",
      "1",
      "",
      "",
      "",
      "",
      "",
      "",
      "ORD-12345",
      "Happy Birthday! Enjoy your gift!",
      "Handle with care - fragile items",
    ],
    [
      "Jane Smith",
      "456 Oak Ave",
      "",
      "Los Angeles",
      "CA",
      "90210",
      "3.0",
      "SKU-003",
      "1",
      "SKU-004",
      "3",
      "SKU-005",
      "1",
      "",
      "",
      "",
      "",
      "ORD-12346",
      "Thank you for your order!",
      "Customer requested expedited shipping",
    ],
  ];

  return { headers, sampleRows };
}

function getKickstarterExample(): CSVExample {
  const headers = [
    "Backer Number",
    "Backer UID",
    "Backer Name",
    "Email",
    "Shipping Country",
    "Shipping Amount",
    "Reward Title",
    "Backing Minimum",
    "Reward ID",
    "Bonus Support",
    "Pledge Amount",
    "Pledged At (UTC)",
    "Late Pledge",
    "Fulfillment Status",
    "Pledged Status",
    "Notes",
    // Product columns - MUST use () format
    // With SKU: (SKU) Product Name
    "(SKU-M) T-Shirt Medium",
    "(SKU-L) T-Shirt Large",
    // Without SKU but still needs (): () Product Name
    "() Sticker Pack",
    "Survey Response (UTC)",
    "Shipping Name",
    "Shipping Address 1",
    "Shipping Address 2",
    "Shipping City",
    "Shipping State",
    "Shipping Postal Code",
    "Shipping Country Name",
    "Shipping Country Code",
    "Shipping Phone Number",
  ];

  const sampleRows = [
    [
      "1001",
      "user123456",
      "John Doe",
      "john@example.com",
      "United States",
      "15.00",
      "Early Bird Special",
      "25",
      "reward001",
      "5.00",
      "30.00",
      "2024-01-15T10:30:00Z",
      "No",
      "unfulfilled",
      "collected",
      "Please ship carefully",
      "1",
      "0",
      "1",
      "1",
      "2024-02-01T14:20:00Z",
      "John Doe",
      "123 Main St",
      "Apt 2B",
      "New York",
      "NY",
      "10001",
      "United States",
      "US",
      "5551234567",
    ],
    [
      "1002",
      "user789012",
      "Jane Smith",
      "jane@example.com",
      "United States",
      "15.00",
      "Standard Reward",
      "25",
      "reward002",
      "0.00",
      "25.00",
      "2024-01-16T09:15:00Z",
      "No",
      "unfulfilled",
      "collected",
      "",
      "0",
      "1",
      "2",
      "1",
      "2024-02-02T11:45:00Z",
      "Jane Smith",
      "456 Oak Ave",
      "",
      "Los Angeles",
      "CA",
      "90210",
      "United States",
      "US",
      "5559876543",
    ],
  ];

  return { headers, sampleRows };
}

function getAdultsAndCraftsExample(): CSVExample {
  const headers = Object.values(AdultsAndCraftsCsvColumns);

  const sampleRows = [
    [
      "ORD-001",
      "2024-01-15",
      "2024-01-15",
      "john@example.com",
      "5551234567",
      "John Doe",
      "John",
      "Doe",
      "",
      "123 Main St",
      "Apt 2B",
      "New York",
      "NY",
      "10001",
      "US",
      "SKU-001",
      "Craft Kit Deluxe",
      "2",
      "23.99",
      "8",
      "",
      "Happy Birthday!",
    ],
    [
      "ORD-001",
      "2024-01-15",
      "2024-01-15",
      "john@example.com",
      "5551234567",
      "John Doe",
      "John",
      "Doe",
      "",
      "123 Main St",
      "Apt 2B",
      "New York",
      "NY",
      "10001",
      "US",
      "SKU-002",
      "Glitter Set",
      "1",
      "9.99",
      "3",
      "",
      "Happy Birthday!",
    ],
  ];

  return { headers, sampleRows };
}

const SAMPLE_VALUES: Partial<Record<OrderImportField, string>> = {
  [OrderImportField.ORDER_NUMBER]: "ORD-001",
  [OrderImportField.ORDER_DATE]: "2024-01-15",
  [OrderImportField.DATE_PAID]: "2024-01-15",
  [OrderImportField.BUYER_EMAIL]: "john@example.com",
  [OrderImportField.BUYER_PHONE]: "5551234567",
  [OrderImportField.RECIPIENT_FULL_NAME]: "John Doe",
  [OrderImportField.RECIPIENT_FIRST_NAME]: "John",
  [OrderImportField.RECIPIENT_LAST_NAME]: "Doe",
  [OrderImportField.RECIPIENT_COMPANY]: "",
  [OrderImportField.ADDRESS_LINE_1]: "123 Main St",
  [OrderImportField.ADDRESS_LINE_2]: "Apt 2B",
  [OrderImportField.CITY]: "New York",
  [OrderImportField.STATE]: "NY",
  [OrderImportField.POSTAL_CODE]: "10001",
  [OrderImportField.COUNTRY_CODE]: "US",
  [OrderImportField.ITEM_SKU]: "SKU-001",
  [OrderImportField.ITEM_NAME]: "Sample Product",
  [OrderImportField.ITEM_QUANTITY]: "2",
  [OrderImportField.ITEM_UNIT_PRICE]: "19.99",
  [OrderImportField.ITEM_WEIGHT_OZ]: "8",
  [OrderImportField.ORDER_WEIGHT_LBS]: "2.5",
  [OrderImportField.ORDER_DIMENSIONS]: "10x8x6",
  [OrderImportField.INTERNAL_NOTES]: "Handle with care",
  [OrderImportField.NOTES_TO_BUYER]: "Thank you for your order!",
  [OrderImportField.GIFT_MESSAGE]: "Happy Birthday!",
};

const SAMPLE_VALUES_ROW2: Partial<Record<OrderImportField, string>> = {
  ...SAMPLE_VALUES,
  [OrderImportField.ORDER_NUMBER]: "ORD-002",
  [OrderImportField.BUYER_EMAIL]: "jane@example.com",
  [OrderImportField.BUYER_PHONE]: "5559876543",
  [OrderImportField.RECIPIENT_FULL_NAME]: "Jane Smith",
  [OrderImportField.RECIPIENT_FIRST_NAME]: "Jane",
  [OrderImportField.RECIPIENT_LAST_NAME]: "Smith",
  [OrderImportField.ADDRESS_LINE_1]: "456 Oak Ave",
  [OrderImportField.ADDRESS_LINE_2]: "",
  [OrderImportField.CITY]: "Los Angeles",
  [OrderImportField.STATE]: "CA",
  [OrderImportField.POSTAL_CODE]: "90210",
  [OrderImportField.ITEM_SKU]: "SKU-002",
  [OrderImportField.ITEM_NAME]: "Another Product",
};

export interface CustomSchemaCSVExample {
  headers: string[];
  rows: Record<string, string>[];
}

export function getCustomSchemaCSVExample(
  mapping: OrderImportColumnMapping,
): CustomSchemaCSVExample {
  const isSingleRow =
    mapping.rowFormat === ImportRowFormat.SINGLE_ROW_WITH_NUMBERED_COLUMNS;
  const maxItems = 5;

  const fields = Object.values(OrderImportField);

  if (isSingleRow) {
    // Build headers: non-item fields first, then item templates expanded for 1..maxItems
    const nonItemHeaders: string[] = [];
    const itemHeaderSlots: string[] = [];

    for (const field of fields) {
      const columnHeader = mapping.fieldMappings[field];
      if (!ITEM_LEVEL_FIELDS.has(field) && columnHeader) {
        nonItemHeaders.push(columnHeader);
      }
    }

    for (let i = 1; i <= maxItems; i++) {
      for (const field of fields) {
        const template = mapping.fieldMappings[field];
        if (ITEM_LEVEL_FIELDS.has(field) && template) {
          itemHeaderSlots.push(template.replace("{n}", String(i)));
        }
      }
    }

    const headers = [...nonItemHeaders, ...itemHeaderSlots];

    const buildSingleRow = (
      sampleValues: Partial<Record<OrderImportField, string>>,
      itemSamples: Partial<Record<OrderImportField, string>>,
    ): Record<string, string> => {
      const row: Record<string, string> = {};
      for (const field of fields) {
        const columnHeader = mapping.fieldMappings[field];
        if (!ITEM_LEVEL_FIELDS.has(field) && columnHeader) {
          row[columnHeader] = sampleValues[field] ?? "";
        }
      }
      for (let i = 1; i <= maxItems; i++) {
        for (const field of fields) {
          const template = mapping.fieldMappings[field];
          if (ITEM_LEVEL_FIELDS.has(field) && template) {
            const expandedHeader = template.replace("{n}", String(i));
            // Only populate first slot with actual data; leave the rest empty
            row[expandedHeader] = i === 1 ? (itemSamples[field] ?? "") : "";
          }
        }
      }
      return row;
    };

    const row1 = buildSingleRow(SAMPLE_VALUES, SAMPLE_VALUES);
    const row2 = buildSingleRow(SAMPLE_VALUES_ROW2, SAMPLE_VALUES_ROW2);

    return { headers, rows: [row1, row2] };
  }

  // MULTIPLE_ROWS_PER_ORDER: item fields appear as normal columns
  const headers: string[] = fields
    .map((field) => mapping.fieldMappings[field])
    .filter((h): h is string => h !== undefined);
  const row1: Record<string, string> = {};
  const row2: Record<string, string> = {};

  for (const field of fields) {
    const columnHeader = mapping.fieldMappings[field];
    if (columnHeader) {
      row1[columnHeader] = SAMPLE_VALUES[field] ?? "";
      row2[columnHeader] = SAMPLE_VALUES_ROW2[field] ?? "";
    }
  }

  // Row 2 shares the same order number (same order, second line item)
  const orderNumberColumn =
    mapping.fieldMappings[OrderImportField.ORDER_NUMBER];
  if (orderNumberColumn) {
    row2[orderNumberColumn] =
      SAMPLE_VALUES[OrderImportField.ORDER_NUMBER] ?? "ORD-001";
  }

  return { headers, rows: [row1, row2] };
}

export function generateCSVContent(example: CSVExample): string {
  const rows = [example.headers, ...example.sampleRows];
  return rows.map((row) => row.map(csvEscapeField).join(",")).join("\n");
}

export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.append(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}
